import { h, toast, confirmDialog, tableScroll, rowAction, rowActions } from '../components/dom.js';
import { buildSearch } from '../components/search.js';
import { buildSortControl } from '../components/sort-control.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import type { Subscription, Profile, Contact } from '../api/types.js';
import { mergeContactsByOwner, type MergedContact } from './merge.js';
import { openContactCardModal } from './contact-card-modal.js';
import { openSubscriberEditor } from './subscriber-editor.js';
import { dateTimeEl } from '../lib/timezone.js';

export async function renderSubscribers(_p: Record<string, string>, query: URLSearchParams): Promise<void> {
  const main = renderShell('/subscribers');
  main.append(pageHead('Subscribers', 'People who receive your live profile details.'));

  const filterProfile = query.get('profile') || '';
  let allSubs: Subscription[] = [];
  let profiles: Profile[] = [];
  // Merged contacts indexed by owner (subscriber) id — so a subscriber who has
  // shared a profile back (i.e. is also in My Contacts) can show their full card.
  let contactByOwner = new Map<string, MergedContact>();

  const load = async (): Promise<void> => {
    const [subsAll, profilesRes, contactsRes] = await Promise.all([
      api.subscribers(), api.profiles(), api.contacts().catch(() => ({ data: [] as Contact[] })),
    ]);
    allSubs = subsAll.data;
    profiles = profilesRes.data;
    contactByOwner = new Map(mergeContactsByOwner(contactsRes.data).map((m) => [m.ownerId, m]));
  };
  await load();

  const names = () => new Map(profiles.map((p) => [p.id, p]));

  // Sort control (shared compact control): by name or by subscribed time, with
  // asc/desc toggle. Applied to the list before the search index (preserves order).
  const sort = buildSortControl(
    [{ value: 'name', label: 'Name', defaultDir: 'asc' },
     { value: 'subscribed', label: 'Subscribed', defaultDir: 'desc' }],
    () => search.setItems(view()));
  const subName = (s: Subscription): string => s.subscriber?.username || s.subscriber?.display_name || '';
  const sortSubs = (list: Subscription[]): Subscription[] => {
    const out = [...list];
    if (sort.key() === 'name') out.sort((a, b) => subName(a).localeCompare(subName(b), undefined, { sensitivity: 'base' }));
    else out.sort((a, b) => (a.since || '').localeCompare(b.since || ''));
    if (sort.dir() === 'desc') out.reverse();
    return out;
  };

  // Filter pills by profile (URL-driven, cross-navigates the page).
  const pills = h('div', { class: 'pill-row', style: 'margin-bottom:16px' },
    h('span', { class: 'muted', style: 'align-self:center' }, h('small', {}, 'Filter by profile:')),
    pill('All', '/subscribers', !filterProfile));
  for (const p of profiles) pills.append(pill(p.name, '/subscribers?profile=' + encodeURIComponent(p.id), filterProfile === p.id));
  main.append(pills);

  const tbody = h('tbody');

  // Rerun the search over freshly-reloaded data (used after a move/add/remove).
  const reload = async (): Promise<void> => { await load(); search.setItems(view()); };
  const view = (): Subscription[] =>
    sortSubs(filterProfile ? allSubs.filter((s) => s.profile_id === filterProfile) : allSubs);

  const openEditor = (s: Subscription): void => {
    // All ACTIVE subscriptions for THIS person (may span several profiles).
    const mine = allSubs.filter((x) => x.subscriber_id === s.subscriber_id && x.status === 'active');
    openSubscriberEditor(s, mine, profiles, reload);
  };

  const search = buildSearch<Subscription>({
    noun: 'subscribers',
    placeholder: 'Search by name, profile, status, or label (or id:<username>)…',
    hint: 'Search matches <strong>name, the profile they see, status, and labels</strong> — entirely on this device, no per-keystroke server calls.',
    blob: (s) => [
      s.subscriber?.display_name,
      s.subscriber?.username,
      names().get(s.profile_id || '')?.name,
      s.status,
      ...(s.labels || []),
    ].filter(Boolean).join(' '),
    pillsFor: (items) => [...new Set(items.flatMap((s) => s.labels || []))].map((l) => ({ label: l, value: l })),
    itemMatchesPill: (s, value) => (s.labels || []).includes(value),
    // Scoped query support: `id:<username>` matches only the VirtualID username.
    fields: { id: (s) => s.subscriber?.username || s.subscriber?.display_name || '' },
    render: (matches) => {
      tbody.replaceChildren(...matches.map((s) => subRow(s, names(), reload, openEditor, contactByOwner.get(s.subscriber_id || ''),
        allSubs.filter((x) => x.subscriber_id === s.subscriber_id && x.status === 'active'), profiles)));
      if (!matches.length) tbody.append(h('tr', {}, h('td', { colspan: '7', class: 'muted' },
        allSubs.length ? 'No subscribers match.' : 'No subscribers.')));
    },
  });
  main.append(search.el);

  // Compact sort control, right-aligned above the table (matches My Contacts).
  main.append(h('div', { class: 'row', style: 'justify-content:flex-end;margin-bottom:12px' }, sort.el));

  main.append(h('div', { class: 'card', style: 'padding:0' },
    tableScroll(h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'VirtualID'), h('th', {}, 'Name'), h('th', {}, 'Profile they see'), h('th', {}, 'Since'),
        h('th', {}, 'Status'), h('th', {}, 'Labels'), h('th', {}, 'Actions'))),
      tbody))));
  main.append(h('p', { class: 'muted', style: 'margin-top:14px' }, h('small', {}, 'Revoking rotates the profile key so the subscriber\u2019s cached copy can no longer be decrypted.')));
  main.append(portalFooter());
  search.setItems(view());
}

function pill(label: string, href: string, active: boolean): HTMLElement {
  return h('a', { class: 'badge ' + (active ? 'badge-brand' : ''), href: '#' + href, style: 'text-decoration:none' }, label);
}

function subRow(s: Subscription, names: Map<string, Profile>, refresh: () => void, openEditor: (s: Subscription) => void, mc?: MergedContact, allSubsForPerson?: Subscription[], profiles?: Profile[]): HTMLElement {
  const prof = names.get(s.profile_id || '');
  const statusBadge = s.status === 'pending'
    ? h('span', { class: 'badge badge-warn' }, 'Pending')
    : s.status === 'active' && s.paused
      ? h('span', { class: 'badge badge-warn' }, 'Disabled')
      : h('span', { class: 'badge ' + (s.status === 'revoked' ? 'badge-warn' : 'badge-ok') }, s.status === 'revoked' ? 'Revoked' : 'Active');

  // "Profile they see" is now clickable — opens the move/add/remove editor.
  const profCell = h('td', {});
  if (s.status === 'active') {
    const link = h('a', { href: '#', style: 'text-decoration:none' },
      prof ? h('span', { class: 'badge ' + (prof.is_public ? 'badge-ok' : 'badge-brand') }, prof.name + ' \u270e') : '\u2014');
    link.addEventListener('click', (e) => { e.preventDefault(); openEditor(s); });
    profCell.append(link);
  } else {
    profCell.append(prof ? h('span', { class: 'badge ' + (prof.is_public ? 'badge-ok' : 'badge-brand') }, prof.name) : '\u2014');
  }

  const actions = h('td', {});
  if (s.status === 'active') {
    const group = rowActions(
      action('Edit', async () => { openEditor(s); }),
      // Enable/Disable toggle: pause stops updates without revoking (reversible).
      // Non-destructive toggle — no confirmation (instant).
      action(s.paused ? 'Enable' : 'Disable', async () => {
        const next = !s.paused;
        await api.setSubscriberPaused(s.id, next);
        toast(next ? 'Subscriber disabled' : 'Subscriber enabled', next ? 'warn' : 'ok');
        refresh();
      }),
      action('Revoke', async () => {
        if (!confirmDialog('Revoke this subscriber? Key rotates; their copy goes stale.')) return;
        await api.revokeSubscriber(s.id); toast('Revoked', 'warn'); refresh();
      }, true),
    );
    actions.append(group);
  }

  // VirtualID cell: the username, clickable -> opens the person's contact card in a
  // modal (same overlay style as the Profiles 'View' preview). Name cell: first +
  // last name when known (from a shared-back contact card), else the display name.
  const virtualId = s.subscriber?.username || s.subscriber?.display_name || 'Subscriber';
  const vidLink = h('a', { href: '#', style: 'text-decoration:none;font-weight:600' }, virtualId);
  vidLink.addEventListener('click', (e) => { e.preventDefault(); openContactCardModal({ contact: mc, username: virtualId, showNavToContact: true, onRemoved: refresh, subscriberSubs: allSubsForPerson, profiles, reloadSubs: async () => { refresh(); } }); });
  const given = (mc?.givenName || '').trim();
  const family = (mc?.familyName || '').trim();
  const fullName = [given, family].filter(Boolean).join(' ')
    || (mc?.displayName && mc.displayName !== virtualId ? mc.displayName : '')
    || (s.subscriber?.display_name && s.subscriber.display_name !== virtualId ? s.subscriber.display_name : '');

  return h('tr', {},
    h('td', {}, vidLink),
    h('td', {}, fullName || h('span', { class: 'muted' }, '\u2014')),
    profCell,
    h('td', {}, s.since ? dateTimeEl(s.since) : '\u2014'),
    h('td', {}, statusBadge),
    h('td', {}, (s.labels || []).map((l) => h('span', { class: 'badge' }, l))),
    actions);
}




function action(label: string, fn: () => Promise<void>, danger = false): HTMLElement {
  return rowAction(label, async () => {
    try { await fn(); } catch (err) { toast((err as Error).message, 'danger'); }
  }, danger ? { variant: 'danger' } : {});
}
